// Pure landing hero resolution, safe for Node SSR and the client.
// Precedence: (1) explicit page hero, (2) reviewed DS family asset,
// (3) a wide real photo from an actual snapshot car, (4) honest neutral hero.
// Never substitutes generic stock or an unrelated make/model/landmark.

const MIN_WIDE_WIDTH = 1200;
const MIN_WIDE_RATIO = 1.4;

const REHOSTED_HOST = /(?:^|\.)(?:r2\.dev|driveshare-assets\.com|r2\.cloudflarestorage\.com)$/i;

/** Only a /files path on a non-rehosted (original site) host is legacy. */
export function isLegacyFilesUrl(url) {
  if (typeof url !== "string") return false;
  try {
    const u = new URL(url);
    return /^\/files(?:\/|$)/i.test(u.pathname) && !REHOSTED_HOST.test(u.hostname);
  } catch {
    return false;
  }
}

function usable(url) {
  return typeof url === "string" && /^https:\/\//i.test(url) && !isLegacyFilesUrl(url) &&
    !/^https:\/\/images\.unsplash\.com\//i.test(url);
}

export function pickWideSnapshotImage(cards) {
  for (const card of cards ?? []) {
    for (const img of card.images ?? []) {
      const w = Number(img.width), h = Number(img.height);
      // Dimensions describe the original/full asset, so prefer the rehosted
      // cdnFull and never label a small medium/thumbnail with them.
      const src = usable(img.cdnFull) ? img.cdnFull : usable(img.url) ? img.url : null;
      if (!src || !(w >= MIN_WIDE_WIDTH) || !(h > 0) || w / h < MIN_WIDE_RATIO) continue;
      const label = [card.year, card.make, card.model].filter(Boolean).join(" ") || card.name || "";
      return { url: src, alt: img.alt || label, width: w, height: h };
    }
  }
  return null;
}

const POSITION_RE = /^(?:(?:\d{1,3}(?:\.\d+)?%|left|right|center|top|bottom)(?: (?:\d{1,3}(?:\.\d+)?%|left|right|center|top|bottom))?)$/;

function safePosition(value, fallback) {
  return typeof value === "string" && POSITION_RE.test(value.trim()) ? value.trim() : fallback;
}

function dim(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

/** Keep a srcset only when every candidate is an approved, usable https URL with a w descriptor. */
export function sanitizeSrcset(srcset) {
  if (typeof srcset !== "string" || !srcset.trim()) return { srcset: "", candidates: [] };
  const candidates = [];
  for (const part of srcset.split(/,\s+(?=https:)/)) {
    const m = part.trim().match(/^(\S+)\s+(\d+)w$/);
    if (!m || !usable(m[1])) return { srcset: "", candidates: [] };
    candidates.push({ url: m[1], width: Number(m[2]) });
  }
  return { srcset: candidates.map((c) => `${c.url} ${c.width}w`).join(", "), candidates };
}

function pickSrc(candidates, fallback, target = 1280) {
  if (!candidates.length) return fallback;
  const ordered = [...candidates].sort((a, b) => a.width - b.width);
  return (ordered.find((c) => c.width >= target) || ordered[ordered.length - 1]).url;
}

export function resolveLandingHero(input) {
  const { page, cards } = input;
  const familyAsset = input.familyAsset || page.reviewed_hero_asset || null;
  const heading = page.hero_heading || page.page_title || page.name || "";
  const base = { srcset: "", objectPosition: "center", src: null };
  const sameAsFamily = familyAsset && usable(familyAsset.url) && familyAsset.url === page.hero_image_url;
  if (usable(page.hero_image_url) && !sameAsFamily) {
    // Different explicit URL: only its own page-level metadata, never the
    // family asset's alt/dimensions (it may show another model).
    const { srcset, candidates } = sanitizeSrcset(page.hero_image_srcset);
    const ownAlt = typeof page.hero_image_alt === "string" && page.hero_image_alt.trim() ? page.hero_image_alt : heading;
    return {
      ...base, url: page.hero_image_url, src: pickSrc(candidates, page.hero_image_url), alt: ownAlt,
      source: "page", srcset, width: dim(page.hero_image_width), height: dim(page.hero_image_height),
      objectPosition: safePosition(page.hero_image_object_position, "center"),
    };
  }
  if (familyAsset && usable(familyAsset.url)) {
    const { srcset, candidates } = sanitizeSrcset(familyAsset.srcset);
    // Reviewed metadata owns alt text: a photo of another model must not be
    // described by the page heading, so a missing alt stays empty.
    return {
      ...base, url: familyAsset.url, src: pickSrc(candidates, familyAsset.url),
      alt: typeof familyAsset.alt === "string" ? familyAsset.alt : "",
      source: "family-asset", srcset,
      width: dim(familyAsset.width), height: dim(familyAsset.height),
      objectPosition: safePosition(familyAsset.objectPosition, "center"),
    };
  }
  const card = pickWideSnapshotImage(cards);
  if (card) {
    return {
      ...base, url: card.url, src: card.url, alt: card.alt || heading, source: "snapshot-card",
      width: card.width, height: card.height, objectPosition: "center 60%",
    };
  }
  return { ...base, url: null, alt: heading, source: "neutral" };
}
